import React, { useCallback } from 'react';
import keyBy from 'lodash/keyBy';

import { Stack } from '@lyearn/ui';
import OptionsList from '@/modules/corporate/modules/questions/fields/components/Player/OptionsList';
import ListItem from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem';
import QuestionText from '@/modules/corporate/modules/questions/fields/components/QuestionText';
import { QuizPlayerFieldProps } from '@/modules/corporate/modules/questions/fields/types';
import { Option, QuestionSubTypeEnum, QuestionTypeEnum } from '@/types/schema';

import { FIRST_KEYCODE_INDEX } from './OptionsListItem/consts';

const EMPTY_ARRAY: never[] = [];

function Player(props: QuizPlayerFieldProps) {
  const { question, onChange, correctAnswers, questionAttempt, classes } = props;
  const optionResponse = questionAttempt?.submission?.optionResponse || EMPTY_ARRAY;

  const isMultiSelect =
    question.subType === QuestionSubTypeEnum.MultiSelect ||
    question.questionType === QuestionTypeEnum.MultiSelect;

  const _onChange = useCallback(
    ({ optionId, optionText }: Option) => {
      if (!onChange) {
        return;
      }

      if (isMultiSelect) {
        let _optionResponse = optionResponse;
        let selectedOptionsById = keyBy(_optionResponse, 'optionId');
        if (selectedOptionsById[optionId]) {
          _optionResponse = _optionResponse.filter((option) => option.optionId !== optionId);
        } else {
          _optionResponse = [..._optionResponse, { optionId, optionText }];
        }
        onChange({ optionResponse: _optionResponse });
      } else {
        const isOptionSelected = optionResponse[0]?.optionId === optionId;
        onChange({ optionResponse: isOptionSelected ? [] : [{ optionId, optionText }] });
      }
    },
    [isMultiSelect, onChange, optionResponse],
  );

  const getKeyCode = useCallback(
    (option: Option, index: number) => {
      if (question.questionType === QuestionTypeEnum.TrueOrFalse) {
        return option.optionText.charAt(0);
      }
      return String.fromCharCode(FIRST_KEYCODE_INDEX + index);
    },
    [question.questionType],
  );

  return (
    <Stack className={onChange ? undefined : 'pointer-events-none'} direction="col" gapY={24}>
      <OptionsList
        ListItem={ListItem}
        correctAnswers={correctAnswers}
        getKeyCode={getKeyCode}
        isWeightedOption={question.subType === QuestionSubTypeEnum.WeightedOption}
        options={(question.options ?? []) as Option[]}
        questionAttempt={questionAttempt}
        renderQuestion={() => (
          <QuestionText classes={{ container: classes?.question }} question={question} />
        )}
        onChange={_onChange}
      />
    </Stack>
  );
}

export default Player;
